import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FlaskConical, ArrowUpDown } from "lucide-react";
import { api } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { VideoHero } from "../components/VideoHero";
import { Reveal } from "../components/Reveal";

// Product films per series — add more entries here as new videos are filmed.
const SERIES_VIDEOS = {
  Lizards: {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/wingman/4ff24718-b121-47ef-b578-026360a1848f/attachments/ba31f6b292534ee083e3a524a2cb8c5d_drift_covert_lizard_v3.mp4",
    label: "Watch It Swim",
    title: "COVERT™ — On The Water",
  },
};

const SERIES_META = {
  Swimbaits: { label: "PHANTOM", name: "PHANTOM SERIES", tagline: "Hydro-Dynamic Paddletail Swimbaits" },
  Lizards: { label: "COVERT", name: "COVERT SERIES", tagline: "Heavy-Cover Creature Baits & Lizards" },
  Worms: { label: "PULSE", name: "PULSE SERIES", tagline: "High-Vibration Finesse Worms & Trick Tails" },
};
const SERIES_ORDER = ["Swimbaits", "Lizards", "Worms"];

const labelFor = (c) => SERIES_META[c]?.label || (c || "").toUpperCase();

const seriesRank = (c) => {
  const i = SERIES_ORDER.indexOf(c);
  return i === -1 ? 90 : i;
};

const SORTS = [
  { id: "featured", label: "Featured First" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price · Low to High" },
  { id: "price-desc", label: "Price · High to Low" },
];

const sortProducts = (list, sort) => {
  const arr = [...list];
  if (sort === "price-asc") return arr.sort((a, b) => (a.price || 0) - (b.price || 0));
  if (sort === "price-desc") return arr.sort((a, b) => (b.price || 0) - (a.price || 0));
  if (sort === "newest") return arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return arr.sort(
    (a, b) =>
      Number(b.featured) - Number(a.featured) ||
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
};

function HeroCard({ product, large = false }) {
  const soldOut = !product.in_stock || product.stock <= 0;
  return (
    <Link
      to={`/product/${product.id}`}
      className={`group relative overflow-hidden bg-[#0C1117] border border-orange-500/25 hover:border-orange-500/60 transition-colors duration-500 flex h-full ${large ? "flex-col md:flex-row" : "flex-col"} ${soldOut ? "opacity-75" : ""}`}
      data-testid={`featured-hero-card-${product.id}`}
    >
      <div className={`${large ? "md:w-1/2 aspect-[4/3] md:aspect-auto" : "aspect-[4/3]"} bg-[#080D12] flex items-center justify-center p-6 border-b ${large ? "md:border-b-0 md:border-r" : ""} border-zinc-800/80`}>
        <img
          src={product.images?.[0]}
          alt={product.name}
          className={`max-h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105 drop-shadow-[0_10px_15px_rgba(0,0,0,0.8)] ${soldOut ? "grayscale" : ""}`}
          loading="lazy"
        />
      </div>
      <div className={`flex flex-col justify-between flex-1 p-6 ${large ? "md:p-10" : ""}`}>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-orange-400 bg-orange-950/80 border border-orange-500/40 px-2 py-0.5">
              Flagship
            </span>
            <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-800 px-2 py-0.5">
              {labelFor(product.category)}
            </span>
            {soldOut && (
              <span className="font-mono-plex text-[10px] tracking-wider uppercase text-zinc-400 bg-zinc-800 border border-zinc-700 px-2 py-0.5">
                Sold Out
              </span>
            )}
          </div>
          <h3 className={`font-display uppercase tracking-tight leading-[0.9] mt-4 ${large ? "text-4xl lg:text-5xl" : "text-2xl"}`}>
            {product.name}
          </h3>
          <p className="mt-3 font-mono-plex text-xs text-zinc-400 tracking-wide">
            {[product.length, product.pack_size].filter(Boolean).join("  //  ")}
          </p>
          {product.colors?.length > 0 && (
            <p className="mt-1 font-mono-plex text-[11px] text-zinc-500 tracking-wide">
              {product.colors.length} color{product.colors.length === 1 ? "" : "s"} available
            </p>
          )}
        </div>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800/60">
          <span className="font-mono-plex text-lg font-bold text-orange-500">${product.price?.toFixed(2)}</span>
          <span className="font-mono-plex text-[11px] tracking-[0.15em] uppercase text-zinc-300 group-hover:text-orange-400 transition-colors inline-flex items-center gap-2">
            {soldOut ? "View Bait" : "View Bait"} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function FeaturedHero({ featured }) {
  if (featured.length === 0) return null;
  return (
    <section className="mt-16" data-testid="featured-hero">
      <Reveal>
        <div className="flex items-end justify-between gap-6 border-b border-zinc-800/80 pb-4">
          <div>
            <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Flagship Baits</span>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Featured Lineup</h2>
          </div>
        </div>
      </Reveal>
      {featured.length === 1 ? (
        <Reveal className="mt-8"><HeroCard product={featured[0]} large /></Reveal>
      ) : featured.length === 2 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {featured.map((p) => <Reveal key={p.id}><HeroCard product={p} /></Reveal>)}
        </div>
      ) : featured.length === 3 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <Reveal className="md:row-span-2"><HeroCard product={featured[0]} /></Reveal>
          <Reveal><HeroCard product={featured[1]} /></Reveal>
          <Reveal><HeroCard product={featured[2]} /></Reveal>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {featured.map((p) => <Reveal key={p.id}><HeroCard product={p} /></Reveal>)}
        </div>
      )}
    </section>
  );
}

function CustomPourBanner({ product }) {
  return (
    <Reveal className="mt-20">
      <Link
        to={`/product/${product.id}`}
        className="group relative block overflow-hidden bg-[#0C1117] border border-orange-500/30 hover:border-orange-500/60 transition-colors duration-500"
        data-testid="custom-pour-banner"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-8 p-8 md:p-12">
          <div className="flex-1">
            <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500 inline-flex items-center gap-2">
              <FlaskConical className="w-4 h-4" /> Custom Pour Studio
            </span>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mt-4">
              Build Your<br />Own Bait
            </h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
              Choose your body, layer dyes and mica pigments, dial in pearl and flake — hand-poured to order, exactly how you want it.
            </p>
          </div>
          {product.images?.[0] && (
            <div className="md:w-64 shrink-0">
              <img src={product.images[0]} alt="Custom Pour Studio" className="w-full aspect-square object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.8)] transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            </div>
          )}
          <div className="shrink-0">
            <span className="inline-flex items-center gap-3 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.15em] uppercase font-bold px-8 py-4 group-hover:bg-orange-600 transition-colors" data-testid="custom-pour-cta">
              Enter Studio <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [colorHex, setColorHex] = useState({});
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("category") || "All";

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/options").then((r) => {
      const m = {};
      r.data.forEach((o) => { if (o.group === "color" && o.hex) m[o.name] = o.hex; });
      setColorHex(m);
    }).catch(() => {});
  }, []);

  const customPour = useMemo(() => products.find((p) => p.custom), [products]);
  const regular = useMemo(() => products.filter((p) => !p.custom), [products]);

  const categories = useMemo(() => {
    const cats = [...new Set(regular.map((p) => p.category).filter((c) => c && c.trim() && c.trim().toLowerCase() !== "all"))];
    return cats.sort((a, b) => seriesRank(a) - seriesRank(b) || a.localeCompare(b));
  }, [regular]);

  const featured = useMemo(
    () =>
      regular
        .filter((p) => p.featured)
        .sort((a, b) => seriesRank(a.category) - seriesRank(b.category) || a.category.localeCompare(b.category)),
    [regular]
  );

  const visible = active === "All" ? regular : regular.filter((p) => p.category === active);

  const sections = useMemo(() => {
    const groups = {};
    visible.forEach((p) => {
      const c = (p.category || "").trim();
      const key = c && c.toLowerCase() !== "all" ? c : "Specialty Pours";
      (groups[key] = groups[key] || []).push(p);
    });
    return Object.keys(groups)
      .sort((a, b) => seriesRank(a) - seriesRank(b) || (a === "Specialty Pours" ? 1 : b === "Specialty Pours" ? -1 : a.localeCompare(b)))
      .map((key) => ({ key, products: sortProducts(groups[key], sort) }));
  }, [visible, sort]);

  const setCat = (c) => {
    if (c === "All") setSearchParams({});
    else setSearchParams({ category: c });
  };

  return (
    <div className="bg-[#04070A] min-h-screen">
      <Nav />
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-36 pb-4">
        <Reveal>
          <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Artisan Baitworks</span>
          <h1 className="font-display text-6xl sm:text-8xl uppercase tracking-tighter mt-4 leading-[0.85]">
            The<br />Armory
          </h1>
          <p className="mt-6 text-sm sm:text-base text-zinc-400 max-w-xl leading-relaxed">
            Precision soft-plastics engineered for high-pressure waters. Every bait hand-poured, every color tuned to get bit.
          </p>
          <div className="flex flex-wrap gap-x-10 gap-y-3 mt-8" data-testid="shop-metrics">
            <span className="font-mono-plex text-xs tracking-widest uppercase text-zinc-500">{products.length}+ Formulations</span>
            <span className="font-mono-plex text-xs tracking-widest uppercase text-zinc-500">100% Hand-Poured</span>
            {customPour && (
              <Link to={`/product/${customPour.id}`} className="font-mono-plex text-xs tracking-widest uppercase text-orange-500 hover:text-orange-400 transition-colors inline-flex items-center gap-2" data-testid="header-studio-link">
                Custom Pour Studio <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </Reveal>

        <div className="flex flex-wrap items-center gap-3 mt-14" data-testid="category-filters">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`font-mono-plex text-xs tracking-[0.15em] uppercase px-5 py-2.5 border transition-colors focus:ring-2 focus:ring-orange-500 focus:outline-none ${
                active === c
                  ? "bg-orange-500 border-orange-500 text-[#04070A] font-bold"
                  : "bg-zinc-900/60 border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
              data-testid={c === "All" ? "filter-all-baits-pill" : `filter-${labelFor(c).toLowerCase().replace(/\s+/g, "-")}-pill`}
            >
              {c === "All" ? "All Baits" : labelFor(c)}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-zinc-900/60 border border-zinc-800 text-zinc-300 font-mono-plex text-xs tracking-widest uppercase px-3 py-2.5 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              data-testid="sort-select"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        {loading ? (
          <p className="mt-16 text-slate-500 font-mono-plex text-sm tracking-widest uppercase">Loading…</p>
        ) : (
          <>
            {active === "All" && <FeaturedHero featured={featured} />}
            {active === "All" && customPour && <CustomPourBanner product={customPour} />}

            {sections.length === 0 ? (
              <p className="mt-16 text-slate-500 font-mono-plex text-sm tracking-widest uppercase">No baits in this series yet.</p>
            ) : (
              sections.map(({ key, products: items }) => {
                const meta = SERIES_META[key];
                return (
                  <section key={key} className="mt-20" data-testid={`series-section-${labelFor(key).toLowerCase().replace(/\s+/g, "-")}`}>
                    <Reveal>
                      <div className="flex items-end justify-between gap-6 border-b border-zinc-800/80 pb-4">
                        <div>
                          <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">
                            {meta ? meta.tagline : "Specialty Pours"}
                          </span>
                          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">
                            {meta ? meta.name : key.toUpperCase()}
                          </h2>
                        </div>
                        <span className="font-mono-plex text-xs tracking-widest text-zinc-500 uppercase shrink-0">
                          {items.length} bait{items.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 mt-8" data-testid="product-grid">
                      {SERIES_VIDEOS[key] && (
                        <Reveal className="sm:col-span-2 xl:col-span-2">
                          <VideoHero
                            {...SERIES_VIDEOS[key]}
                            className="border border-white/10 h-full"
                            data-testid={`series-video-${labelFor(key).toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        </Reveal>
                      )}
                      {items.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} colorHex={colorHex} />
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
